import type { GameState, Tile } from "@dailywars/core/engine";
import { coordKey, isBuildingTile, MAX_HP, normalizedHpDisplay } from "@dailywars/core/engine";

import type { CanvasBoardAnimation } from "#/game/animation/types";
import { getHpBadgeImage, isHpBadgeValue } from "#/game/badgeSprites";
import { drawBuildingHitFlash, drawBuildingImage } from "#/game/buildingDraw";
import { isImageReady } from "#/game/imageReady";
import { drawRoadAutotileImage } from "#/game/roadAutotileDraw";
import { resolveRoadAutotile } from "#/game/roadSprites";
import { drawRoadTerrainBackground } from "#/game/roadTerrainBackground";
import { terrainKindForTile } from "#/game/roadTileIcon";
import { getBuildingImage, getOccupiedBuildingImage } from "#/game/svgAssets";
import { drawTerrainTile } from "#/game/terrainTileDraw";

import { getCannonDeathDrawEffect, getUnitAttackFlash } from "./boardAnimations";
import { tileIndex, TILE_SIZE } from "./boardConstants";

export type MapDrawState = Pick<GameState, "width" | "height" | "tiles" | "theme">;

function drawCannonHpBadge({
  ctx,
  tile,
  x,
  y,
}: {
  ctx: CanvasRenderingContext2D;
  tile: Tile;
  x: number;
  y: number;
}): void {
  if (tile.kind !== "cannon") return;
  const hpDisplay = normalizedHpDisplay(tile.hp ?? MAX_HP);
  if (!isHpBadgeValue(hpDisplay)) return;
  const badge = getHpBadgeImage(hpDisplay);
  if (!isImageReady(badge)) return;
  ctx.drawImage(badge, x * TILE_SIZE, y * TILE_SIZE, TILE_SIZE, TILE_SIZE);
}

export function drawTerrainLayer(ctx: CanvasRenderingContext2D, state: MapDrawState): void {
  for (let y = 0; y < state.height; y++) {
    for (let x = 0; x < state.width; x++) {
      const tile = state.tiles[tileIndex({ x, y, width: state.width })];
      if (tile.kind === "road") {
        drawRoadTerrainBackground({
          ctx,
          tiles: state.tiles,
          width: state.width,
          x,
          y,
          theme: state.theme,
          tileSize: TILE_SIZE,
        });
        continue;
      }
      const terrainKind = terrainKindForTile(tile.kind);
      drawTerrainTile({
        ctx,
        kind: terrainKind,
        x,
        y,
        theme: state.theme,
        tileSize: TILE_SIZE,
        tiles: state.tiles,
        width: state.width,
      });
    }
  }
}

export function drawRoadStripesLayer(ctx: CanvasRenderingContext2D, state: MapDrawState): void {
  for (let y = 0; y < state.height; y++) {
    for (let x = 0; x < state.width; x++) {
      const tile = state.tiles[tileIndex({ x, y, width: state.width })];
      if (tile.kind !== "road") continue;
      const { img, rotation } = resolveRoadAutotile({
        tiles: state.tiles,
        width: state.width,
        x,
        y,
        theme: state.theme,
      });
      if (isImageReady(img)) {
        drawRoadAutotileImage({ ctx, img, tileX: x, tileY: y, tileSize: TILE_SIZE, rotation });
      }
    }
  }
}

export function drawBuildingsLayer({
  ctx,
  state,
  occupiedTileKeys,
  animation = null,
  nowMs = 0,
}: {
  ctx: CanvasRenderingContext2D;
  state: MapDrawState;
  occupiedTileKeys?: ReadonlySet<string>;
  animation?: CanvasBoardAnimation | null;
  nowMs?: number;
}): void {
  for (let y = 0; y < state.height; y++) {
    for (let x = 0; x < state.width; x++) {
      const tile = state.tiles[tileIndex({ x, y, width: state.width })];
      if (!isBuildingTile(tile.kind)) continue;
      const isOccupied = occupiedTileKeys?.has(coordKey({ x, y })) ?? false;
      const bImg = isOccupied
        ? getOccupiedBuildingImage(tile.kind, tile.owner ?? "neutral")
        : getBuildingImage(tile.kind, tile.owner ?? "neutral");
      if (!isImageReady(bImg)) continue;

      drawBuildingImage({ ctx, img: bImg, tileX: x, tileY: y, tileSize: TILE_SIZE });
      if (tile.kind === "cannon") {
        drawBuildingHitFlash({
          ctx,
          img: bImg,
          tileX: x,
          tileY: y,
          tileSize: TILE_SIZE,
          hitFlash: getUnitAttackFlash({ pos: { x, y }, animation, nowMs }),
        });
      }
      drawCannonHpBadge({ ctx, tile, x, y });
    }
  }
  drawGhostCannons({ ctx, state, occupiedTileKeys, animation, nowMs });
}

function drawGhostCannons({
  ctx,
  state,
  occupiedTileKeys,
  animation,
  nowMs,
}: {
  ctx: CanvasRenderingContext2D;
  state: MapDrawState;
  occupiedTileKeys?: ReadonlySet<string>;
  animation: CanvasBoardAnimation | null;
  nowMs: number;
}): void {
  const ghosts =
    animation?.type === "move" || animation?.type === "attackFx"
      ? (animation.ghostCannons ?? [])
      : [];
  for (const ghost of ghosts) {
    const live = state.tiles[tileIndex({ x: ghost.pos.x, y: ghost.pos.y, width: state.width })];
    if (live?.kind === "cannon") continue;
    const deathEffect = getCannonDeathDrawEffect({
      pos: ghost.pos,
      animation,
      nowMs,
    });
    if (deathEffect && !deathEffect.showUnit) continue;
    drawGhostCannonBuilding({
      ctx,
      tile: ghost.tile,
      x: ghost.pos.x,
      y: ghost.pos.y,
      occupiedTileKeys,
      hitFlash: getUnitAttackFlash({ pos: ghost.pos, animation, nowMs }),
    });
  }
}

function drawGhostCannonBuilding({
  ctx,
  tile,
  x,
  y,
  occupiedTileKeys,
  hitFlash,
}: {
  ctx: CanvasRenderingContext2D;
  tile: Tile;
  x: number;
  y: number;
  occupiedTileKeys?: ReadonlySet<string>;
  hitFlash: number;
}): void {
  if (tile.kind !== "cannon") return;
  const isOccupied = occupiedTileKeys?.has(coordKey({ x, y })) ?? false;
  const bImg = isOccupied
    ? getOccupiedBuildingImage(tile.kind, tile.owner ?? "neutral")
    : getBuildingImage(tile.kind, tile.owner ?? "neutral");
  if (!isImageReady(bImg)) return;
  drawBuildingImage({ ctx, img: bImg, tileX: x, tileY: y, tileSize: TILE_SIZE });
  drawBuildingHitFlash({
    ctx,
    img: bImg,
    tileX: x,
    tileY: y,
    tileSize: TILE_SIZE,
    hitFlash,
  });
  drawCannonHpBadge({ ctx, tile, x, y });
}

export function drawMapLayers(ctx: CanvasRenderingContext2D, state: MapDrawState): void {
  drawTerrainLayer(ctx, state);
  drawRoadStripesLayer(ctx, state);
  drawBuildingsLayer({ ctx, state });
}
